import { isIOS } from "./utils";

// Every onError handler across every <video> call site in this app
// previously just flipped a boolean ("can't play this video") - the real
// video.error (MediaError) was never read, so a network failure, a decode
// failure, an unsupported codec, and a stale auth-token 401 were all
// indistinguishable. This is the one place that reads it, so future
// playback reports are triageable instead of "video doesn't play" with no
// further detail. Deliberately logs no filename/URL/token - only the file
// extension, which is enough to spot a codec/container pattern without
// exposing anything private.

const MEDIA_ERROR_NAMES: Record<number, string> = {
  1: "MEDIA_ERR_ABORTED",
  2: "MEDIA_ERR_NETWORK",
  3: "MEDIA_ERR_DECODE",
  4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
};

function bufferedSummary(video: HTMLVideoElement): string {
  const ranges: string[] = [];
  for (let i = 0; i < video.buffered.length; i++) {
    ranges.push(`${video.buffered.start(i).toFixed(1)}-${video.buffered.end(i).toFixed(1)}`);
  }
  return ranges.length ? ranges.join(",") : "none";
}

function platformOf(): string {
  if (isIOS()) return "ios";
  if (/Android/.test(navigator.userAgent)) return "android";
  return "other";
}

// `context` identifies which of this app's several independent video call
// sites reported the failure (e.g. "lightbox", "chat-bubble",
// "gift-viewer", "watch-together-sync") - there is no shared player
// component today, so this is what makes the log line attributable at all.
export function logVideoPlaybackError(context: string, video: HTMLVideoElement | null, filename: string, extra?: Record<string, unknown>): void {
  const ext = filename.toLowerCase().replace(".enc", "").split(".").pop() || "unknown";
  const err = video?.error ?? null;
  console.warn("VIDEO_PLAYBACK_ERROR", {
    context,
    platform: platformOf(),
    userAgent: navigator.userAgent,
    container: ext,
    errorCode: err ? MEDIA_ERROR_NAMES[err.code] || `unknown(${err.code})` : "none",
    errorMessage: err?.message || "",
    networkState: video?.networkState ?? null,
    readyState: video?.readyState ?? null,
    currentTime: video ? Math.round(video.currentTime * 10) / 10 : null,
    duration: video && Number.isFinite(video.duration) ? Math.round(video.duration * 10) / 10 : null,
    buffered: video ? bufferedSummary(video) : "n/a",
    visibilityState: document.visibilityState,
    ...extra,
  });
}

// Watch Together's remote-play path specifically wants to know when a
// play() promise rejects, independent of a real "error" event (a rejected
// autoplay attempt never fires `error` at all - the element is fine, the
// BROWSER refused the call). Same shape, no MediaError to read since
// there isn't one.
export function logVideoPlayRejection(context: string, video: HTMLVideoElement | null, filename: string, reason: unknown): void {
  const ext = filename.toLowerCase().replace(".enc", "").split(".").pop() || "unknown";
  const reasonName = reason instanceof Error ? reason.name : typeof reason;
  const reasonMessage = reason instanceof Error ? reason.message : String(reason);
  console.warn("VIDEO_PLAY_REJECTED", {
    context,
    platform: platformOf(),
    userAgent: navigator.userAgent,
    container: ext,
    reasonName,
    reasonMessage,
    networkState: video?.networkState ?? null,
    readyState: video?.readyState ?? null,
    visibilityState: document.visibilityState,
  });
}
